// The knowledge layer's durable row models (data-model.md:45-47; D40/D41; ADR-0019).
//
// Postgres (control-plane, FORCE RLS) is the DURABLE SOURCE OF TRUTH for the knowledge graph
// and chunks; FalkorDB (graph) and Qdrant (vectors) are DERIVED, rebuildable projections
// (D40/D41 - never pgvector-as-knowledge-store, and never the reverse: the graph/vector stores
// are rebuilt FROM these rows). One ontology across every scope (knowledge-architecture.md:24-31):
// the tenant graph (app + founder scopes) plus the separate global and Nightcrew-internal
// substrate graphs all share this vocabulary.
//
// Isolation shows up in the shape: `accountId` is null ONLY for the shared global and the
// substrate scopes; `appId` is null for founder/global/substrate. A tenant read is RLS-filtered
// on `account_id` (data-model.md:7); this schema does not itself enforce isolation.
import { z } from "zod";
import { AccountId, AppId, KnowledgeChunkId, KnowledgeEdgeId, KnowledgeNodeId } from "../ids.js";
import { EmbeddingVector } from "../ports/retrieval.js";
import { JsonObject, NonEmptyString, Timestamp } from "./common.js";
// --- The ontology enums (data-model.md:45-46; knowledge-architecture.md:29) ---
/**
 * Node types in the one ontology (data-model.md:45). Enforced here at the boundary; the Wave-2
 * db lane mirrors it as a CHECK/enum (app-engineering §2). Widen only through an ADR + the
 * contract owner.
 */
export const NodeType = z.enum([
    "feature",
    "flow",
    "component",
    "data_entity",
    "dependency",
    "endpoint",
    "risk",
    "fix",
    "convention",
    "pattern",
    "agent",
    "skill",
    "check",
]);
/**
 * Edge relations. The wire set is deliberately CLOSED (ADR-0019 decision 2): the seven relations
 * named across knowledge-architecture.md:29 (a CLOSED list) and data-model.md:46 (open-ended,
 * "..."), including `implements` which knowledge-architecture.md:29 adds for the tenant->global
 * pointer. Widening is an ADDITIVE contract change, never a silent one.
 */
export const RelationType = z.enum([
    "depends_on",
    "part_of",
    "holds_pii",
    "touched_by",
    "feeds",
    "implements",
    "similar_to",
]);
/** The four knowledge scopes (data-model.md:45). `substrate` is Nightcrew-internal (the kit). */
export const KnowledgeScope = z.enum(["app", "founder", "global", "substrate"]);
/** Where a chunk's text came from (data-model.md:47 knowledge_chunks.source). */
export const KnowledgeChunkSource = z.enum(["code", "fix_rationale", "report", "doc"]);
// --- Row models (data-model.md:45-47) ---
/**
 * A knowledge_nodes row (data-model.md:45). `accountId` is null for the global + substrate
 * scopes; `appId` is null for founder/global/substrate. `props` is the jsonb prop bag.
 */
export const KnowledgeNode = z.object({
    id: KnowledgeNodeId,
    accountId: AccountId.nullable(),
    appId: AppId.nullable(),
    scope: KnowledgeScope,
    type: NodeType,
    label: NonEmptyString,
    props: JsonObject,
    updatedAt: Timestamp,
});
/**
 * A knowledge_edges row. Mirrors data-model.md:46 EXACTLY: `{id, scope, from_node, to_node,
 * relation, props}`. data-model.md:46 lists NO account_id on knowledge_edges (unlike
 * knowledge_nodes at :45): `scope` is the tenancy-relevant column present here, and an edge's
 * account/app tenancy is carried by its endpoint nodes (`fromNode`/`toNode` both reference
 * knowledge_nodes rows that DO carry account_id + app_id, :45). Isolation is enforced at the
 * node/query level - GraphStore.neighborhood takes accountId + appId and returns only edges
 * between in-scope nodes (ADR-0019 decision 3) - so the edge row needs no account_id to be
 * tenant-safe. Directed: `fromNode` -> `toNode` under `relation`.
 */
export const KnowledgeEdge = z.object({
    id: KnowledgeEdgeId,
    scope: KnowledgeScope,
    fromNode: KnowledgeNodeId,
    toNode: KnowledgeNodeId,
    relation: RelationType,
    props: JsonObject,
});
/**
 * A knowledge_chunks row (data-model.md:47). `embedding` is the durable pgvector column
 * (Postgres is the source of truth, D40/D41); it is null until the incremental indexer embeds
 * the chunk (knowledge-architecture.md:71), and it is projected to Qdrant for search, never
 * serialized to a customer surface. `accountId`/`appId` are null for the shared global scope.
 */
export const KnowledgeChunk = z.object({
    id: KnowledgeChunkId,
    accountId: AccountId.nullable(),
    appId: AppId.nullable(),
    scope: KnowledgeScope,
    source: KnowledgeChunkSource,
    body: NonEmptyString,
    embedding: EmbeddingVector.nullable(),
    nodeId: KnowledgeNodeId.nullable(),
    updatedAt: Timestamp,
});
//# sourceMappingURL=knowledge.js.map